import { memo, useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import _map from 'lodash/map';
// Local files
import FormAutocomplete from 'components/Common/FormAutocomplete/FormAutocomplete';
import LocationsAutocomplete from 'components/Locations/Autocomplete/Autocomplete';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useCustomSelector from 'hooks/useCustomSelector';
import useClinicians from 'hooks/useClinicians';
import useApp from 'hooks/useApp';
import useSuccess from 'hooks/useSuccess';
import { DELETE_CLINICIAN } from 'helpers/confirmations';
import { checkEmptyString } from 'helpers';
import { cliniciansSelectors } from 'features/clinicians';
import { appSelectors } from 'features/app'

const CliniciansDrawer = () => {
  const mediaDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));
  const { openDeleteDialog, setClinicianDrawer } = useApp();
  const { createClinician, updateClinician } = useClinicians();
  const { setSuccess } = useSuccess();
  const fetchedClinician = useCustomSelector(cliniciansSelectors.getClinicianData);
  const { open, edit } = useCustomSelector(appSelectors.getClinicianDrawerData);
  const fetching = useCustomSelector(cliniciansSelectors.clinicianIsFetching);
  const [firstName, setFirstName] = useState({ value: '', error: '' });
  const [lastName, setLastName] = useState({ value: '', error: '' });
  const [displayName, setDisplayName] = useState({ value: '', error: '' });
  const [injuries, setInjuries] = useState([]);
  const [locations, setLocations] = useState({ value: [], formattedValue: [], error: '' });
  const [prompt, setPrompt] = useState({ value: '', error: '' });
  const [filterPrompt, setFilterPrompt] = useState({ value: '', error: '' });
  const [mergePrompt, setMergePrompt] = useState({ value: '', error: '' });

  const handleClose = () => setClinicianDrawer({ open: false, edit: false });
  const handleEntering = () => {
    if (edit) {
      setFirstName({ value: fetchedClinician?.first_name, error: '' });
      setLastName({ value: fetchedClinician?.last_name, error: '' });
      setDisplayName({ value: fetchedClinician?.display_name ?? '', error: '' });
      setInjuries(fetchedClinician?.injuries || []);
      setLocations({value: _map(fetchedClinician.locations, ({id}) => id), formattedValue: fetchedClinician.locations, error: '' });
      setPrompt({ value: fetchedClinician?.prompt, error: '' });
      setFilterPrompt({ value: fetchedClinician?.filter_prompt, error: '' });
      setMergePrompt({ value: fetchedClinician?.merge_prompt, error: '' });
    } 
  };
  const handleExited = () => { 
    setFirstName({ value: '', error: '' });
    setLastName({ value: '', error: '' });
    setDisplayName({ value: '', error: '' });
    setInjuries([]);
    setLocations({ value: [], formattedValue: [], error: '' });
    setPrompt({ value: '', error: '' });
    setFilterPrompt({ value: '', error: '' });
    setMergePrompt({ value: '', error: '' });
  };
  const needToUpdate = (a,b) => a.first_name !== b.first_name || 
                                a.last_name !== b.last_name || 
                                a.display_name !== b.display_name || 
                                a.injuries !== b.injuries || 
                                a.locations !== b.localLocations || 
                                a.prompt !== b.prompt ||
                                a.filter_prompt !== b.filter_prompt ||
                                a.merge_prompt !== b.merge_prompt;
  const handleSave = () => {
    let hasError = false;

    if (!checkEmptyString(firstName.value)) {
      setFirstName(prev => ({ ...prev, error: `First Name can't be empty` }) );
      hasError = true;
    }
    if (!checkEmptyString(lastName.value)) {
      setLastName(prev => ({ ...prev, error: `Last Name can't be empty` }) );
      hasError = true;
    }
    if (!checkEmptyString(prompt.value)) {
      setPrompt(prev => ({ ...prev, error: `Prompt can't be empty` }) );
      hasError = true;
    }
    
    if (hasError) return;

    const clinician = { 
      ...(edit && { id: fetchedClinician.id }),
      first_name: firstName.value, 
      last_name: lastName.value, 
      display_name: displayName.value,
      injuries: injuries, 
      location_ids: locations.value,
      localLocations: locations.formattedValue,
      ...(!edit && { prompt: prompt.value }), 
      ...(!edit && { filter_prompt: filterPrompt.value }),
      ...(!edit && { merge_prompt: mergePrompt.value }),
    };

    if (edit) {
      if (needToUpdate(fetchedClinician, clinician)) {
        updateClinician(clinician)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Clinician successfully updated');
            handleClose();
          }
        })
      } else {  
        handleClose();
      }
    } else {
      createClinician(clinician)
      .then(({ error }) => {
        if (!error) {
          setSuccess('Clinician successfully created');
          handleClose();
        }
      })
    }
  };
  
  const handleDelete = () => {
    handleClose();
    openDeleteDialog({ id: fetchedClinician.id, type: DELETE_CLINICIAN });
  };

  return (
    <Drawer
      anchor={!mediaDesktop ? 'bottom' : 'right'}
      open={open}
      onClose={handleClose}
      SlideProps={{ onExited: handleExited, onEntering: handleEntering }}
      ModalProps={{ sx: { zIndex: 1300 } }}
      PaperProps={{
        sx: {
          height: '100%',
          width: { xs: '100%', md: '600px'},
          backgroundColor: 'background.default'
        }
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', p: '8px 8px 0' }}>
          <Button color='inherit' sx={{ fontWeight: 700 }} onClick={handleClose}>Close</Button>
          <div style={{ flexGrow: 1 }}/>
          {edit && <Button color='error' disabled={fetching} onClick={handleDelete} sx={{ fontWeight: 700 }}>Delete</Button>}
        </Box>
        <Box sx={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center'}}>
          {!edit ? 'Create' : 'Edit'} Clinician
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: '16px 100px 32px' } }}>
        <TextField
          fullWidth
          margin='normal'
          label='First Name'
          disabled={fetching}
          value={firstName.value}
          onChange={({ target: { value } }) => setFirstName({ value, error: '' })}
          helperText={firstName.error}
          error={!!firstName.error}
        />
        <TextField
          fullWidth
          margin='normal'
          label='Last Name'
          disabled={fetching}
          value={lastName.value}
          onChange={({ target: { value } }) => setLastName({ value, error: '' })}
          helperText={lastName.error}
          error={!!lastName.error}
        />
        <FormAutocomplete
          id='injuries'
          label='Injuries'
          options={[]}
          value={injuries}
          onChange={(_, options) => setInjuries(options)}
          multiple
          symbol=''
          freeSolo
          renderOption={(props, option) => <li {...props}>{option}</li>}
        />
        <TextField
          fullWidth
          margin='normal'
          label='Display Name'
          disabled={fetching}
          value={displayName.value}
          onChange={({ target: { value } }) => setDisplayName({ value, error: '' })}
          helperText={displayName.error}
          error={!!displayName.error}
        />
        <LocationsAutocomplete
          id='category'
          label='Select categories'
          fullWidth
          multiple
          categoryType='exercise'
          margin='dense'
          value={locations.formattedValue}
          error={!!locations.error}
          helperText={locations.error}
          onChange={({ value, formattedValue, error }) => setLocations({ value, formattedValue, error })}
        />
        {!edit && <TextField
          fullWidth
          margin='normal'
          label='Filter Prompt'
          multiline
          minRows={3}
          maxRows={5}
          disabled={fetching}
          value={filterPrompt.value}
          onChange={({ target: { value } }) => setFilterPrompt({ value, error: '' })}
          helperText={filterPrompt.error}
          error={!!filterPrompt.error}
        />}
        {!edit && <TextField
          fullWidth
          margin='normal'
          label='Merge Prompt'
          multiline
          minRows={3}
          maxRows={5}
          disabled={fetching}
          value={mergePrompt.value}
          onChange={({ target: { value } }) => setMergePrompt({ value, error: '' })}
          helperText={mergePrompt.error}
          error={!!mergePrompt.error}
        />}
        {!edit && <TextField
          fullWidth
          margin='normal'
          label='Scheduling Rules'
          multiline
          minRows={3}
          maxRows={5}
          disabled={fetching}
          value={prompt.value}
          onChange={({ target: { value } }) => setPrompt({ value, error: '' })}
          helperText={prompt.error}
          error={!!prompt.error}
        />}
        <Button
          variant='contained'
          sx={{ display: 'flex', mx: 'auto', px: 5, mt: 3 }}
          disabled={fetching}
          onClick={handleSave}
        >
          Save
        </Button>
      </Box>
    </Drawer>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <CliniciansDrawer {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);